import firebase from 'firebase/app';
import 'firebase/firestore';

import { config } from 'src/config';
import type { VersionDocument } from 'src/modules/designStudio/database/documentTypes';
import { withFirebaseErrorSentry } from 'src/shared/telemetry/sentry';

import type { SetHistoryEvent } from './types';

let isEmulatorInitialized = false;
const initializeEmulator = (
  firestore: firebase.firestore.Firestore,
  host: string,
) => {
  if (isEmulatorInitialized) {
    return;
  }

  try {
    const { hostname, port } = new URL(host);

    // This setting is required to run cypress tests
    // https://stackoverflow.com/questions/59336720/cant-use-cypress-to-test-app-using-using-firestore-local-emulator
    firestore.settings({ experimentalForceLongPolling: true });
    firestore.useEmulator(hostname, parseInt(port, 10));
    isEmulatorInitialized = true;
    // eslint-disable-next-line no-console
    console.info(`🤡 🔥 · Connected to Firestore emulator @ ${host}`);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      '🤡 ❗ · EMULATOR_FIREBASE_FIRESTORE was detected, but an error occurred connecting to the emulator host.',
      'Make sure EMULATOR_FIREBASE_FIRESTORE is a valid url (e.g: "http://localhost:8080)\n',
      error,
    );
  }
};

const getOrCreateModelDocument = async (jobId: number) => {
  const firestore = firebase.firestore();

  const { EMULATOR_FIREBASE_FIRESTORE: emulatorHost } = config;
  if (emulatorHost) initializeEmulator(firestore, emulatorHost);

  const collection = firestore.collection('models');
  const query = collection.where('job_id', '==', jobId);

  let querySnapshot = await query.get();

  if (querySnapshot.empty) {
    await withFirebaseErrorSentry(() =>
      collection.doc().set({
        job_id: jobId,
        created_at: firebase.firestore.Timestamp.now(),
      }),
    )();

    querySnapshot = await withFirebaseErrorSentry(() => query.get())();
  }

  return querySnapshot;
};

const batchMarkVersionsAsNotCurrent = (versions: VersionDocument[] | null) => {
  const firestore = firebase.firestore();
  const batch = firestore.batch();
  // update all versions in history to have current false;
  // current represents what is being "currently" shown to the user
  versions?.forEach(version => batch.update(version.ref, { current: false }));
  return batch;
};

/* Model Reference queries */

const getLatestVersions = async (
  modelReference: firebase.firestore.DocumentReference,
  userId: number,
) =>
  withFirebaseErrorSentry(() =>
    modelReference
      .collection('versions')
      .where('author_id', '==', userId)
      .orderBy('created_at')
      .limitToLast(1)
      .get(),
  )();

const listenForVersionsBySession = ({
  ref,
  userId,
  sessionId,
  callback,
}: {
  ref: firebase.firestore.DocumentReference;
  userId: number | null;
  sessionId?: string | null;
  callback: (event: SetHistoryEvent) => void;
}) => {
  const unsubscribe = ref
    ?.collection('versions')
    .where('author_id', '==', userId)
    .where('session_id', '==', sessionId)
    .orderBy('created_at')
    .onSnapshot(snapshot =>
      callback({ type: 'SET_HISTORY', data: snapshot.docs }),
    );
  return unsubscribe;
};

const listenForVersions = ({
  ref,
  userId,
  callback,
}: {
  ref: firebase.firestore.DocumentReference;
  userId: number | null;
  sessionId?: string | null;
  callback: (event: SetHistoryEvent) => void;
}) => {
  const unsubscribe = ref
    ?.collection('versions')
    .where('author_id', '==', userId)
    .orderBy('created_at')
    .onSnapshot(snapshot =>
      callback({ type: 'SET_HISTORY', data: snapshot.docs }),
    );
  return unsubscribe;
};

export {
  getOrCreateModelDocument,
  batchMarkVersionsAsNotCurrent,
  getLatestVersions,
  listenForVersionsBySession,
  listenForVersions,
};
